import { DragDropContext, Draggable, DropResult, Droppable, ResponderProvided } from "@hello-pangea/dnd";
import {
  LocationPageInterface,
  LocationVarInheritance,
  LocationVarKey,
  LocationVarWithId,
  MergedLocationVar,
  SpotDataButton,
  SpotDataButtonStyle
} from "@swivl-mono/types";
import { Button, ButtonVariant, InheritancePill, Spinner, SpotLocationButton } from "@swivl-mono/ui";
import { nanoid } from "nanoid";
import React from "react";

import LocationVarsModel from "../../../../Models/Variables/LocationVars.Model";

interface Props {
  buttons?: any;
  buttonsVar?: MergedLocationVar;
  setSelectedButtonId(id: string): void;
  setButtons(data: SpotDataButton[]): void;
  selectedButtonId?: string;
  page: LocationPageInterface;
}

export function SpotButtonsView(props: Props) {
  const { buttons, setSelectedButtonId, selectedButtonId, buttonsVar, page } = props;
  const [isLoading, setIsLoading] = React.useState(false);

  if (!buttonsVar) {
    return <Spinner />;
  }
  const onDragEnd = (result: DropResult, provided: ResponderProvided) => {
    if (!result.destination) {
      return;
    }
    const items = [...buttons];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    props.setButtons(items);
  };

  const customize = async () => {
    if (!buttonsVar) {
      return;
    }
    setIsLoading(true);
    await LocationVarsModel.createVariable(LocationVarKey.SpotButtons, buttonsVar.value, props.page.groupId, props.page.locationId);
    setIsLoading(false);
  };

  const editable = buttonsVar.inheritance === LocationVarInheritance.NONE;

  const buttonViews = buttons
    ? buttons.map((item, index) => {
        return (
          <Draggable draggableId={"draggable_" + index} index={index}>
            {(provided, snapshot) => (
              <div
                key={item.id}
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                className={selectedButtonId === item.id ? "mb-2 rounded-lg bg-orange-50 px-1 pt-1 pb-1" : "mb-2"}
                onClick={() => {
                  setSelectedButtonId(item.id);
                }}
              >
                <SpotLocationButton key={item.id} button={item} builderButton={true} />
              </div>
            )}
          </Draggable>
        );
      })
    : null;

  return (
    <div className="relative w-full">
      {!editable && (
        <div
          className="customizeOverlay bg-blue/90 absolute inset-0 z-50 flex items-center justify-center"
          onClick={() => {
            if (!isLoading) {
              customize();
            }
          }}
        >
          <div className="text-lg font-bold">{isLoading ? <Spinner /> : "Customize"}</div>
        </div>
      )}
      <DragDropContext onDragEnd={onDragEnd} onDragStart={(e: any) => {}}>
        <div className="relative z-10 mx-auto flex w-full flex-col items-center justify-center overflow-hidden bg-white px-5 md:max-w-2xl md:px-10">
          <Droppable droppableId={"spot_dropable"} type="BUTTON">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                className="w-full"
                style={{ backgroundColor: snapshot.isDraggingOver ? "#C3BCB9" : "transparent", borderRadius: 3, padding: "0 0 20px 0" }}
                {...provided.droppableProps}
              >
                {provided.placeholder}
                {buttonViews}
              </div>
            )}
          </Droppable>
          {editable && (
            <div className="flex w-full items-center justify-center">
              <Button
                fullWidth
                variant={ButtonVariant.DASHED}
                onClick={() => {
                  props.setButtons([
                    ...buttons,
                    {
                      id: nanoid(8),
                      title: "New Button",
                      href: "#",
                      icon: "",
                      style: SpotDataButtonStyle.PRIMARY
                    }
                  ]);
                }}
              >
                Add Button
              </Button>
            </div>
          )}

          {buttonsVar && buttonsVar.id && buttonsVar.inheritance !== LocationVarInheritance.DEFAULT && (
            <div className="mb-3 pt-2">
              <InheritancePill
                alwaysShow={true}
                inheritance={buttonsVar.inheritance}
                isEditing={true}
                canRevert={true}
                customText={{
                  fromGroup: "Inherited from Group",
                  fromOrg: "Inherited from Org",
                  default: "Inherited from default"
                }}
                onClick={function (): void {
                  if (confirm("Are you sure you want to revert these buttons?")) {
                    const locationVarWithId: LocationVarWithId = {
                      id: buttonsVar.id!,
                      key: buttonsVar.key,
                      value: buttonsVar.value
                    };
                    LocationVarsModel.deleteVariable(locationVarWithId);
                  }
                }}
              />
            </div>
          )}
        </div>
      </DragDropContext>
    </div>
  );
}
